/**
 * Verification Node Config Panel
 *
 * Displayed when a Liveness Check or ID Document node is selected in the workflow builder.
 * Allows per-workflow override of tenant-level verification settings:
 * - Tier selection (basic/premium)
 * - Liveness mode (active/passive/both) - only for liveness nodes with basic tier
 */

import React, { useState, useMemo } from 'react';
import { usePricing, formatPricePerUnit } from '../../hooks/usePricing';

// Static tier structure - prices are filled in dynamically from the API
const BASE_TIER_OPTIONS = {
  ocr: [
    { value: 'basic', featureId: 'id_verification', label: 'Basic', accuracy: '~85%', description: 'Fast text extraction for common formats', unit: 'doc' },
    { value: 'premium', featureId: 'document_ocr_premium', label: 'Premium', accuracy: '~98%', description: 'High-accuracy with handwriting support', unit: 'doc' },
  ],
  liveness: [
    { value: 'basic', featureId: 'passive_liveness', label: 'Basic', accuracy: '~92%', description: 'Detects photos and basic spoofs', unit: 'check' },
    { value: 'premium', featureId: 'active_liveness', label: 'Premium', accuracy: '~99%', description: 'Defeats masks, deepfakes, advanced spoofs', unit: 'check' },
  ],
};

const LIVENESS_MODES = [
  { value: 'active', label: 'Active (face challenges)', description: 'User turns head left/right and blinks' },
  { value: 'passive', label: 'Passive (anti-spoof)', description: 'Analyzes video frames for spoofing' },
  { value: 'both', label: 'Both', description: 'Run both active and passive checks' },
];

export default function VerificationNodeConfig({ node, onUpdate }) {
  const { data: pricing } = usePricing();

  // Build tier options with dynamic prices from the API
  const TIER_OPTIONS = useMemo(() => {
    const features = pricing?.features || [];
    const getFeature = (id) => features.find((f) => f.id === id);

    return {
      ocr: BASE_TIER_OPTIONS.ocr.map((tier) => ({
        ...tier,
        price: formatPricePerUnit(getFeature(tier.featureId), tier.unit),
      })),
      liveness: BASE_TIER_OPTIONS.liveness.map((tier) => ({
        ...tier,
        price: formatPricePerUnit(getFeature(tier.featureId), tier.unit),
      })),
    };
  }, [pricing]);

  // Backwards-compatible detection of liveness vs document nodes
  const componentId = node.data.component_id || node.data.componentType || '';
  const nodeId = node.id || '';
  const label = (node.data.label || '').toLowerCase();

  const isLiveness = (
    componentId === 'liveness' ||
    nodeId === 'liveness' ||
    label.includes('liveness')
  );

  const config = node.data.config || {};

  const [overrideGlobal, setOverrideGlobal] = useState(config.override_verification || false);
  const [primitiveTier, setPrimitiveTier] = useState(config.primitive_tier || 'basic');
  const [livenessMode, setLivenessMode] = useState(config.liveness_mode || 'active');

  const updateConfig = (updates) => {
    const newConfig = {
      override_verification: overrideGlobal,
      primitive_tier: primitiveTier,
      ...(isLiveness ? { liveness_mode: livenessMode } : {}),
      ...updates,
    };
    onUpdate({
      ...node,
      data: {
        ...node.data,
        config: { ...node.data.config, ...newConfig },
      },
    });
  };

  const handleOverrideToggle = (e) => {
    const enabled = e.target.checked;
    setOverrideGlobal(enabled);
    updateConfig({ override_verification: enabled });
  };

  const handleTierChange = (tier) => {
    setPrimitiveTier(tier);
    updateConfig({ primitive_tier: tier });
  };

  const handleModeChange = (e) => {
    const mode = e.target.value;
    setLivenessMode(mode);
    updateConfig({ liveness_mode: mode });
  };

  const tierOptions = isLiveness ? TIER_OPTIONS.liveness : TIER_OPTIONS.ocr;

  return (
    <div className="verification-node-config">
      <div className="form-group">
        <label className="checkbox override-toggle">
          <input type="checkbox" checked={overrideGlobal} onChange={handleOverrideToggle} />
          <span>Override tenant verification settings</span>
        </label>
        {!overrideGlobal && (
          <div className="global-hint">Using tenant-level verification settings from Settings → Verification.</div>
        )}
      </div>

      {overrideGlobal && (
        <>
          <div className="form-group">
            <label>{isLiveness ? 'Liveness' : 'OCR'} Tier</label>
            <div className="tier-options">
              {tierOptions.map((option) => (
                <label
                  key={option.value}
                  className={`tier-option ${primitiveTier === option.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="primitive_tier"
                    value={option.value}
                    checked={primitiveTier === option.value}
                    onChange={() => handleTierChange(option.value)}
                  />
                  <div className="tier-content">
                    <div className="tier-header">
                      <span className="tier-name">{option.label}</span>
                      <span className="tier-price">{option.price}</span>
                    </div>
                    <div className="tier-accuracy">{option.accuracy} accuracy</div>
                    <span className="tier-desc">{option.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {isLiveness && primitiveTier === 'basic' && (
            <div className="form-group">
              <label>Liveness Mode</label>
              <select
                value={livenessMode}
                onChange={handleModeChange}
                className="mode-select"
              >
                {LIVENESS_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
              <div className="mode-hint">
                {LIVENESS_MODES.find((m) => m.value === livenessMode)?.description}
              </div>
            </div>
          )}
        </>
      )}

      <style>{`
        .verification-node-config {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .override-toggle span {
          font-weight: 500;
        }
        .global-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          padding: 6px 8px;
          background: var(--bg-tertiary);
          border-radius: 4px;
        }
        .tier-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 8px;
        }
        .tier-option {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 12px;
          background: var(--bg-tertiary);
          border: 2px solid var(--border-color);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .tier-option:hover {
          border-color: var(--border-hover, rgba(255,255,255,0.2));
        }
        .tier-option.selected {
          border-color: var(--accent-primary);
          background: var(--accent-glow-subtle, rgba(45, 212, 191, 0.08));
        }
        .tier-option input[type="radio"] {
          margin-top: 2px;
          accent-color: var(--accent-primary);
        }
        .tier-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .tier-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .tier-name {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .tier-price {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 4px;
          background: var(--bg-elevated, rgba(255,255,255,0.08));
          color: var(--text-secondary);
        }
        .tier-option.selected .tier-price {
          background: var(--accent-primary);
          color: white;
        }
        .tier-accuracy {
          font-size: 10px;
          font-weight: 600;
          color: var(--accent-primary);
          margin-bottom: 2px;
        }
        .tier-desc {
          font-size: 11px;
          color: var(--text-secondary);
        }
        .mode-select {
          width: 100%;
          padding: 8px 12px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 12px;
          color: var(--text-primary);
          cursor: pointer;
          margin-top: 4px;
        }
        .mode-select option {
          background: var(--bg-primary);
          color: var(--text-primary);
        }
        .mode-hint {
          font-size: 10px;
          color: var(--text-secondary);
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
}
